/**
 * Conteúdo da aba Mensagens no relatório.
 * Exibe listagem de mensagens (email, SMS, WhatsApp, aplicativo) com filtros e ações.
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  TextField,
  Grid,
  useTheme,
  TablePagination,
  TableSortLabel,
  FormControl,
  InputLabel,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Select,
  alpha,
} from '@mui/material';
import {
  Search,
  Refresh,
  Email,
  Sms,
  WhatsApp,
  NotificationsActive,
  CheckCircle,
  Error,
  Schedule,
  Replay,
  Visibility,
  Delete,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import mensagemService, { Mensagem, MensagemFilters } from '../../services/mensagemService';
import WhatsappViewMsg from '../../components/modals/WhatsappViewMsg';
import EmailViewMsg from '../../components/modals/EmailViewMsg';
import SmsViewMsg from '../../components/modals/SmsViewMsg';
import { useRelatorioCores } from './relatorioUtils';

const RelatorioMensagensTab: React.FC = () => {
  const theme = useTheme();
  const cores = useRelatorioCores();

  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalMensagens, setTotalMensagens] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [tipoFilter, setTipoFilter] = useState<string>('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [stats, setStats] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [selectedMensagem, setSelectedMensagem] = useState<Mensagem | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [whatsappViewOpen, setWhatsappViewOpen] = useState(false);
  const [emailViewOpen, setEmailViewOpen] = useState(false);
  const [smsViewOpen, setSmsViewOpen] = useState(false);
  const [orderBy, setOrderBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const loadMensagens = async () => {
    try {
      setLoading(true);
      setError(null);
      const filters: MensagemFilters = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy: orderBy,
        sortOrder: order.toUpperCase() as 'ASC' | 'DESC',
      };
      if (statusFilter) filters.status = statusFilter as any;
      if (tipoFilter) filters.tipo = tipoFilter as any;
      if (searchTerm) filters.search = searchTerm;
      if (dataInicio) filters.data_inicio = dataInicio;
      if (dataFim) filters.data_fim = dataFim;

      const response = await mensagemService.getMensagens(filters);
      if (response.success && response.data) {
        setMensagens(response.data);
        setTotalMensagens(response.pagination?.total || 0);
        setTotalPages(response.pagination?.pages || 0);
      } else {
        setError(response.message || 'Erro ao carregar mensagens');
      }
    } catch (err) {
      console.error('Erro ao carregar mensagens:', err);
      setError('Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setLoadingStats(true);
      const response = await mensagemService.getStats(dataInicio, dataFim);
      if (response.success && response.data) setStats(response.data);
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleResend = async (id: number) => {
    try {
      const result = await Swal.fire({
        title: 'Reenviar mensagem?',
        text: 'A mensagem será marcada para reenvio',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, reenviar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: theme.palette.primary.main,
      });
      if (result.isConfirmed) {
        const response = await mensagemService.resendMensagem(id);
        if (response.success) {
          Swal.fire({ icon: 'success', title: 'Sucesso!', text: 'Mensagem marcada para reenvio', timer: 2000 });
          loadMensagens();
        } else {
          Swal.fire({ icon: 'error', title: 'Erro!', text: response.message || 'Erro ao reenviar mensagem' });
        }
      }
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: 'Erro!', text: err?.message || 'Erro ao reenviar mensagem' });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const result = await Swal.fire({
        title: 'Deletar mensagem?',
        text: 'Esta ação não pode ser desfeita',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, deletar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: theme.palette.error.main,
      });
      if (result.isConfirmed) {
        const response = await mensagemService.deleteMensagem(id);
        if (response.success) {
          Swal.fire({ icon: 'success', title: 'Deletado!', text: 'Mensagem deletada com sucesso', timer: 2000 });
          loadMensagens();
        } else {
          Swal.fire({ icon: 'error', title: 'Erro!', text: response.message || 'Erro ao deletar mensagem' });
        }
      }
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: 'Erro!', text: err?.message || 'Erro ao deletar mensagem' });
    }
  };

  const handleViewDetails = async (id: number) => {
    try {
      const response = await mensagemService.getMensagemById(id);
      if (response.success && response.data) {
        setSelectedMensagem(response.data);
        switch (response.data.tipo) {
          case 'whatsapp': setWhatsappViewOpen(true); break;
          case 'email': setEmailViewOpen(true); break;
          case 'sms': setSmsViewOpen(true); break;
          default: setDetailsDialogOpen(true); break;
        }
      }
    } catch {
      Swal.fire({ icon: 'error', title: 'Erro!', text: 'Erro ao carregar detalhes da mensagem' });
    }
  };

  useEffect(() => { loadMensagens(); }, [page, rowsPerPage, statusFilter, tipoFilter, orderBy, order]);
  useEffect(() => { loadStats(); }, [dataInicio, dataFim]);
  useEffect(() => {
    const t = setTimeout(() => { if (page === 0) loadMensagens(); else setPage(0); }, 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'enviado': return <CheckCircle fontSize="small" />;
      case 'aguardando_envio': return <Schedule fontSize="small" />;
      case 'enfileirado': return <Schedule fontSize="small" />;
      case 'falhou': return <Error fontSize="small" />;
      default: return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'enviado': return 'success';
      case 'aguardando_envio': return 'warning';
      case 'enfileirado': return 'info';
      case 'falhou': return 'error';
      default: return 'default';
    }
  };

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'email': return <Email fontSize="small" />;
      case 'sms': return <Sms fontSize="small" />;
      case 'whatsapp': return <WhatsApp fontSize="small" />;
      case 'aplicativo': return <NotificationsActive fontSize="small" />;
      default: return null;
    }
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'email': return 'primary';
      case 'sms': return 'secondary';
      case 'whatsapp': return 'success';
      case 'aplicativo': return 'info';
      default: return 'default';
    }
  };

  const formatStatus = (status: string) => {
    const m: Record<string, string> = { enviado: 'Enviado', aguardando_envio: 'Aguardando Envio', enfileirado: 'Na fila de envio', falhou: 'Falhou' };
    return m[status] || status;
  };

  const formatTipo = (tipo: string) => {
    const m: Record<string, string> = { email: 'E-mail', sms: 'SMS', whatsapp: 'WhatsApp', aplicativo: 'Aplicativo' };
    return m[tipo] || tipo;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  return (
    <Box>
      {!loadingStats && stats && (
        <Grid container spacing={1.5} sx={{ mb: 1 }}>
          {stats.porStatus?.map((item: any) => (
            <Grid item xs={12} sm={6} md={3} key={item.status}>
              <Paper elevation={2} sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">{formatStatus(item.status)}</Typography>
                <Typography variant="h6" fontWeight={700}>{item.total || 0}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Paper elevation={2} sx={{ p: 1.5, mb: 1, borderRadius: 2 }}>
        <Grid container spacing={0.7} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Buscar"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><Search sx={{ fontSize: 18 }} /></InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="enviado">Enviado</MenuItem>
                <MenuItem value="aguardando_envio">Aguardando Envio</MenuItem>
                <MenuItem value="enfileirado">Na fila de envio</MenuItem>
                <MenuItem value="falhou">Falhou</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select value={tipoFilter} onChange={(e) => setTipoFilter(e.target.value)} label="Tipo">
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="email">E-mail</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                <MenuItem value="aplicativo">Aplicativo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2.3}>
            <TextField fullWidth size="small" type="date" label="Data Início" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={2.3}>
            <TextField fullWidth size="small" type="date" label="Data Fim" value={dataFim} onChange={(e) => setDataFim(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={0.4}>
            <Tooltip title="Atualizar">
              <IconButton size="small" onClick={() => { loadMensagens(); loadStats(); }} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 550 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (t) => `color-mix(in srgb, ${cores.aguardando} 12%, ${t.palette.background.paper})`,
                  fontWeight: 700,
                  fontSize: '0.73rem',
                  py: 0.8,
                  borderBottom: `2px solid ${alpha(cores.aguardando, 0.2)}`,
                },
              }}>
                <TableCell>ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Destino</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'createdAt'}
                    direction={orderBy === 'createdAt' ? order : 'asc'}
                    onClick={() => { setOrder(order === 'asc' ? 'desc' : 'asc'); setOrderBy('createdAt'); }}
                  >
                    Data Criação
                  </TableSortLabel>
                </TableCell>
                <TableCell>Data Envio</TableCell>
                <TableCell>Tentativas</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 2 }}><CircularProgress /></TableCell>
                </TableRow>
              ) : mensagens.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.secondary">Nenhuma mensagem encontrada</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                mensagens.map((m) => (
                  <TableRow key={m.id} hover sx={{ '&:hover': { bgcolor: alpha(cores.aguardando, 0.03) } }}>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{m.id}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Chip icon={getStatusIcon(m.status) || undefined} label={formatStatus(m.status)} color={getStatusColor(m.status) as any} size="small" />
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Chip icon={getTipoIcon(m.tipo) || undefined} label={formatTipo(m.tipo)} color={getTipoColor(m.tipo) as any} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{m.destino}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{m.cliente ? m.cliente.nome : '-'}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{formatDate(m.createdAt)}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{formatDate(m.data_envio)}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{m.tentativas}</TableCell>
                    <TableCell align="center" sx={{ py: 0.5 }}>
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Ver detalhes">
                          <IconButton size="small" color="primary" onClick={() => handleViewDetails(m.id)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {m.status === 'falhou' && (
                          <Tooltip title="Reenviar">
                            <IconButton size="small" color="warning" onClick={() => handleResend(m.id)}>
                              <Replay fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Deletar">
                          <IconButton size="small" color="error" onClick={() => handleDelete(m.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 0.8,
            px: 2,
            bgcolor: alpha(cores.aguardando, 0.04),
            borderTop: `1px solid ${alpha(cores.aguardando, 0.15)}`,
            position: 'relative',
            flexWrap: 'nowrap',
            gap: 1,
          }}
        >
          <Box sx={{ position: { xs: 'static', md: 'absolute' }, left: 20, display: { xs: 'none', md: 'block' } }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              Total de mensagens: <strong>{totalMensagens}</strong>
            </Typography>
          </Box>
          <TablePagination
            component="div"
            count={totalMensagens}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPageOptions={[10, 25, 50, 100]}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            labelRowsPerPage="Linhas por pág:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              width: '100%',
              '.MuiTablePagination-toolbar': {
                minHeight: 32,
                flexWrap: 'nowrap',
                justifyContent: 'center',
                gap: 1,
                p: 0,
                '& .MuiTablePagination-spacer': { display: 'none' },
              },
              '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.78rem' },
              '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.78rem' },
              '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.78rem' },
              '.MuiTablePagination-actions': { ml: 0.5, '& .MuiIconButton-root': { p: 0.5 } },
            }}
          />
        </Box>
      </Paper>

      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalhes da Mensagem</DialogTitle>
        <DialogContent>
          {selectedMensagem && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">ID</Typography>
                  <Typography variant="body1">{selectedMensagem.id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip icon={getStatusIcon(selectedMensagem.status) || undefined} label={formatStatus(selectedMensagem.status)} color={getStatusColor(selectedMensagem.status) as any} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Tipo</Typography>
                  <Chip icon={getTipoIcon(selectedMensagem.tipo) || undefined} label={formatTipo(selectedMensagem.tipo)} color={getTipoColor(selectedMensagem.tipo) as any} size="small" variant="outlined" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Destino</Typography>
                  <Typography variant="body1">{selectedMensagem.destino}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Conteúdo</Typography>
                  <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>{selectedMensagem.conteudo}</Typography>
                  </Paper>
                </Grid>
                {selectedMensagem.cliente && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Cliente</Typography>
                    <Typography variant="body1">{selectedMensagem.cliente.nome}</Typography>
                  </Grid>
                )}
                {selectedMensagem.usuario && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Enviado por</Typography>
                    <Typography variant="body1">{selectedMensagem.usuario.nome} ({selectedMensagem.usuario.usuario})</Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Data de Criação</Typography>
                  <Typography variant="body1">{formatDate(selectedMensagem.createdAt)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Data de Envio</Typography>
                  <Typography variant="body1">{formatDate(selectedMensagem.data_envio)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Tentativas</Typography>
                  <Typography variant="body1">{selectedMensagem.tentativas}</Typography>
                </Grid>
                {selectedMensagem.erro_mensagem && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="error">Erro</Typography>
                    <Alert severity="error" sx={{ mt: 1 }}>{selectedMensagem.erro_mensagem}</Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <WhatsappViewMsg open={whatsappViewOpen} onClose={() => setWhatsappViewOpen(false)} mensagem={selectedMensagem} />
      <EmailViewMsg open={emailViewOpen} onClose={() => setEmailViewOpen(false)} mensagem={selectedMensagem} />
      <SmsViewMsg open={smsViewOpen} onClose={() => setSmsViewOpen(false)} mensagem={selectedMensagem} />
    </Box>
  );
};

export default RelatorioMensagensTab;
